import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { useSnackbar } from 'notistack';
// mui
import { Box, Stack, Container, Stepper, Step, StepLabel } from '@mui/material';
// zui
import { Button } from 'src/@/components/ui/button';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import { Label } from 'src/@/components/ui/label';
import { Input } from 'src/@/components/ui/input';
import { RowInput } from 'src/components/forms/RowInput';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Slider } from 'src/@/components/ui/slider';
import { Textarea } from 'src/@/components/ui/textarea';
import { Switch } from 'src/@/components/ui/switch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
// actions
import v2_actionAiConfig from 'src/actions/v2/ai/v2_actionAiConfig';
import v2_actionAgenteAi from 'src/actions/v2/ai/v2_actionAgenteAi';
// components
import AiAgentTraining from './tabs/AiAgentTraining';
import AiAgentProfileTab from './tabs/AiAgentProfileTab';
import AiAgentConfigTab from './tabs/AiAgentConfigTab';
import { ScrollArea, ScrollBar } from 'src/@/components/ui/scroll-area';
import AiSelectPromptType from './tabs/AiSelectPromptType';
import AiAgentPromptTab from './tabs/AiAgentPromptTab';
import v2_actionEditarAgenteAi from 'src/actions/v2/ai/v2_actionEditarAgenteAi';
import useAuth from 'src/hooks/useAuth';
import Scrollbar from 'src/components/Scrollbar';
import v2_actionCalendarios from 'src/actions/v2/calendario/v2_actionCalendarios';
import AiAgentPlaygroundTab from './tabs/AiAgentPlaygroundTab';
import AiAgentFeedbackTab from './tabs/AiAgentFeedbackTab';
import AiAgentModulosTab from './tabs/AiAgentModulosTab';

// ----------------------------------------------------------------------

const steps = ['Escopo', 'Perfil', 'Prompt', 'Configurações', 'Treinamento'];

export default function AgenteAiDetails() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const [config, setConfig] = useState(null);
    const [aiAgent, setAiAgent] = useState(null);
    const [calendars, setCalendars] = useState([]);
    const [loading, setLoading] = useState(false);
    const { cliente, user } = useAuth();
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('id');
    const hasModulesFeature = Array.isArray(user?.features) && user.features.includes('modules');

    async function buscarConfigAi() {
        try {
            const response = await v2_actionAiConfig(cliente._id);
            setConfig(response.data.data);
        } catch (error) { }
    }

    async function getListCalendars() {
        try {
            const response = await v2_actionCalendarios(cliente._id);
            if (response.status === 200) {
                setCalendars(response.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    }

    async function buscarAgenteAi(id) {
        try {
            const response = await v2_actionAgenteAi(id);
            setAiAgent(response.data.data);
        } catch (error) { }
    }

    async function handleEditarAgenteAi() {
        try {
            let data = { action: 'update', scope: aiAgent };
            await v2_actionEditarAgenteAi(aiAgent._id, data);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    useEffect(() => {
        buscarAgenteAi(code);
        buscarConfigAi();
        getListCalendars();
        return () => {
            setAiAgent(null);
            setConfig(null);
        };
    }, [code]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await handleEditarAgenteAi();
            enqueueSnackbar('Agente editado com sucesso', { variant: 'success' });
            await buscarAgenteAi(code);
        } catch (error) {
            enqueueSnackbar('Erro ao editar agente', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                        <Button
                            size="icon"
                            variant="outline"
                            className="w-9 h-9 rounded-full bg-white border-gray-200 shadow-sm"
                            onClick={() => {
                                navigate('/dashboard/configuracoes/agentes-ia');
                            }}
                        >
                            <Iconify icon="majesticons:arrow-left" className="text-gray-700" />
                        </Button>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{aiAgent?.name || 'Agente IA'}</h1>
                    </div>
                    <p className="text-sm text-gray-500 pl-12">Gerencie o perfil, prompt e configurações do seu Agente de IA.</p>
                </div>
                <ButtonLoading
                    loading={loading}
                    onClick={handleSave}
                    className="!h-auto flex items-center gap-2 px-6 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95 border-none"
                    variant="default"
                >
                    Salvar Alterações
                </ButtonLoading>
            </div>

            <div className="flex flex-col gap-4">
                <Tabs defaultValue="perfil" className="w-full">
                    <TabsList className={`grid ${hasModulesFeature ? 'w-[820px] grid-cols-7' : 'w-[720px] grid-cols-6'} h-12 bg-gradient-to-b from-slate-50 to-slate-100 bg-opacity-50 border border-violet-200 text-sm font-medium`}>
                        <TabsTrigger value="perfil">Perfil</TabsTrigger>
                        <TabsTrigger value="prompt">Prompt</TabsTrigger>
                        <TabsTrigger value="config">Configurações</TabsTrigger>
                        {hasModulesFeature && <TabsTrigger value="modulos">Módulos</TabsTrigger>}
                        <TabsTrigger value="treinamento">Treinamento</TabsTrigger>
                        <TabsTrigger value="playground">Playground</TabsTrigger>
                        <TabsTrigger value="feedback">Feedback</TabsTrigger>
                    </TabsList>

                    <div className="mt-8">
                        <TabsContent value="escopo">
                            <AiSelectPromptType
                                source="details"
                                changePromptType={(value) => {
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            generalConfig: {
                                                ...aiAgent.config.generalConfig,
                                                type: value,
                                            },
                                        },
                                    });
                                }}
                                aiAgent={aiAgent}
                            />
                        </TabsContent>

                        <TabsContent value="perfil">
                            <AiAgentProfileTab
                                aiAgent={aiAgent}
                                config={config}
                                changeName={(value) => setAiAgent({ ...aiAgent, name: value })}
                                changeCompanyName={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config.promptGuide,
                                                companyName: value,
                                            },
                                        },
                                    })
                                }
                                changePersona={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config.promptGuide,
                                                persona: value,
                                            },
                                        },
                                    })
                                }
                                changeTemperature={(value) => {
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            generalConfig: {
                                                ...aiAgent.config.generalConfig,
                                                temperature: value[0],
                                            },
                                        },
                                    });
                                }}
                                changeAgentFunction={(value) => {
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            generalConfig: {
                                                ...aiAgent.config.generalConfig,
                                                agent_function_id: value,
                                            },
                                        },
                                    });
                                }}
                                changeTypeCommunication={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config.promptGuide,
                                                type_speaking_id: value,
                                            },
                                        },
                                    })
                                }
                                changeBusinessActivity={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config.promptGuide,
                                                business_activities_id: value,
                                            },
                                        },
                                    })
                                }
                                changeAgentTypeSpeaking={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config.promptGuide,
                                                type_speaking_id: value,
                                            },
                                        },
                                    })
                                }
                            />
                        </TabsContent>

                        <TabsContent value="prompt">
                            <AiAgentPromptTab
                                prompt={aiAgent?.config?.promptGuide?.prompt}
                                aiAgent={aiAgent}
                                changePrompt={(value) => {
                                    if (aiAgent?.config?.generalConfig.type === 'promptFree') {
                                        setAiAgent({
                                            ...aiAgent,
                                            config: {
                                                ...aiAgent.config,
                                                promptFree: {
                                                    ...aiAgent.config.promptFree,
                                                    prompt: value,
                                                },
                                            },
                                        });
                                    } else {
                                        setAiAgent({
                                            ...aiAgent,
                                            config: {
                                                ...aiAgent.config,
                                                promptGuide: {
                                                    ...aiAgent.config.promptGuide,
                                                    prompt: value,
                                                },
                                            },
                                        });
                                    }
                                }}
                            />
                        </TabsContent>

                        <TabsContent value="config">
                            <div className="h-[70vh] w-full flex">
                                <Scrollbar className="w-full h-full">
                                    <AiAgentConfigTab
                                        user={user}
                                        changeAgentType={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        agent_type: value,
                                                    },
                                                },
                                            })
                                        }
                                        aiAgent={aiAgent}
                                        awnserAudio={aiAgent?.config?.generalConfig?.awnserAudio}
                                        changeAwnserAudio={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        awnserAudio: value,
                                                    },
                                                },
                                            })
                                        }
                                        listTypeVoices={config?.voices}
                                        calendars={calendars}
                                        changeListenMsgs={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        timeListenMsgs: value ? Number(value) : value,
                                                    },
                                                },
                                            })
                                        }
                                        changeDelayBetweenMsgs={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        delayBetweenMsgs: value ? Number(value) : value,
                                                    },
                                                },
                                            })
                                        }
                                        changeLengthSendVoice={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        lengthSendVoice: value ? Number(value) : value,
                                                    },
                                                },
                                            })
                                        }
                                        changeLimitSendVoice={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        limitSendVoice: value,
                                                    },
                                                },
                                            })
                                        }
                                        changeLastContext={(value) => {
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        lastContext: value,
                                                    },
                                                },
                                            });
                                        }}
                                        changeConfigTools={(event) => {
                                            const { name, value } = event;
                                            if (name === 'toolCalendarStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            tools: {
                                                                ...aiAgent.config.generalConfig.tools,
                                                                calendar: { ...aiAgent.config.generalConfig.tools.calendar, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'toolCalendarId') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            tools: {
                                                                ...aiAgent.config.generalConfig.tools,
                                                                calendar: { ...aiAgent.config.generalConfig.tools.calendar, calendar_id: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'toolCalendarMsgConfirm') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            tools: {
                                                                ...aiAgent.config.generalConfig.tools,
                                                                calendar: { ...aiAgent.config.generalConfig.tools.calendar, toolCalendarMsgConfirm: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'knowledgeCalendarStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            knowledge: {
                                                                ...aiAgent.config.generalConfig.knowledge,
                                                                calendar: { ...aiAgent.config.generalConfig.knowledge?.calendar, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'knowledgeKnowledgeBaseStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            knowledge: {
                                                                ...aiAgent.config.generalConfig.knowledge,
                                                                knowledgeBase: { ...aiAgent.config.generalConfig.knowledge?.knowledgeBase, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'knowledgeConversationHistoryStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            knowledge: {
                                                                ...aiAgent.config.generalConfig.knowledge,
                                                                conversationHistory: {
                                                                    ...aiAgent.config.generalConfig.knowledge?.conversationHistory,
                                                                    status: value,
                                                                },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'knowledgeProductsStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            knowledge: {
                                                                ...aiAgent.config.generalConfig.knowledge,
                                                                products: { ...aiAgent.config.generalConfig.knowledge?.products, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'knowledgeFaqStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            knowledge: {
                                                                ...aiAgent.config.generalConfig.knowledge,
                                                                faq: { ...aiAgent.config.generalConfig.knowledge?.faq, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            } else if (name === 'customModelAi') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: { ...aiAgent.config.generalConfig, customModelAi: value },
                                                    },
                                                });
                                            } else if (name === 'toolProductsStatus') {
                                                setAiAgent({
                                                    ...aiAgent,
                                                    config: {
                                                        ...aiAgent.config,
                                                        generalConfig: {
                                                            ...aiAgent.config.generalConfig,
                                                            tools: {
                                                                ...aiAgent.config.generalConfig.tools,
                                                                products: { ...aiAgent.config.generalConfig.tools?.products, status: value },
                                                            },
                                                        },
                                                    },
                                                });
                                            }
                                        }}
                                        onChangeMsgs={(event) => {
                                            const { name, value } = event.target;
                                            switch (name) {
                                                case 'msgs.msgBreakFiles':
                                                    setAiAgent({
                                                        ...aiAgent,
                                                        config: {
                                                            ...aiAgent.config,
                                                            generalConfig: {
                                                                ...aiAgent.config.generalConfig,
                                                                messages: { ...aiAgent.config.generalConfig.messages, msgBreakFiles: value },
                                                            },
                                                        },
                                                    });
                                                    break;
                                                case 'msgs.msgImages':
                                                    setAiAgent({
                                                        ...aiAgent,
                                                        config: {
                                                            ...aiAgent.config,
                                                            generalConfig: {
                                                                ...aiAgent.config.generalConfig,
                                                                messages: { ...aiAgent.config.generalConfig.messages, msgImages: value },
                                                            },
                                                        },
                                                    });
                                                    break;
                                                case 'msgs.msgBreakAudios':
                                                    setAiAgent({
                                                        ...aiAgent,
                                                        config: {
                                                            ...aiAgent.config,
                                                            generalConfig: {
                                                                ...aiAgent.config.generalConfig,
                                                                messages: { ...aiAgent.config.generalConfig.messages, msgBreakAudios: value },
                                                            },
                                                        },
                                                    });
                                                    break;
                                                default:
                                                    break;
                                            }
                                        }}
                                        changeAllowsInChat={(value) =>
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        allowsInChat: value,
                                                    },
                                                },
                                            })
                                        }
                                        changeVoice={(value) => {
                                            setAiAgent({
                                                ...aiAgent,
                                                config: {
                                                    ...aiAgent.config,
                                                    generalConfig: {
                                                        ...aiAgent.config.generalConfig,
                                                        voice_id: value,
                                                    },
                                                },
                                            });
                                        }}
                                        voice={aiAgent?.config?.generalConfig?.voice_id}
                                    />
                                </Scrollbar>
                            </div>
                        </TabsContent>
                        {hasModulesFeature && <TabsContent value="modulos">
                            <AiAgentModulosTab
                                aiAgent={aiAgent}
                                changeModuleAgentType={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            module_agent_type: value,
                                        },
                                    })
                                }
                                changePersona={(value) =>
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            promptGuide: {
                                                ...aiAgent.config?.promptGuide,
                                                persona: value,
                                            },
                                        },
                                    })
                                }
                                changeModuleConfig={(patch) => {
                                    const moduleKey = aiAgent?.config?.module_agent_type;
                                    if (!moduleKey) return;
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            module: {
                                                ...aiAgent.config?.module,
                                                [moduleKey]: {
                                                    ...aiAgent.config?.module?.[moduleKey],
                                                    ...patch,
                                                },
                                            },
                                        },
                                    });
                                }}
                                changeTomConversa={(value) => {
                                    const moduleKey = aiAgent?.config?.module_agent_type;
                                    if (!moduleKey) return;
                                    setAiAgent({
                                        ...aiAgent,
                                        config: {
                                            ...aiAgent.config,
                                            module: {
                                                ...aiAgent.config?.module,
                                                [moduleKey]: {
                                                    ...aiAgent.config?.module?.[moduleKey],
                                                    conversa: {
                                                        ...aiAgent.config?.module?.[moduleKey]?.conversa,
                                                        tom: value,
                                                    },
                                                },
                                            },
                                        },
                                    });
                                }}
                            />
                        </TabsContent>}
                        <TabsContent value="treinamento">
                            <AiAgentTraining />
                        </TabsContent>
                        <TabsContent value="playground">
                            <AiAgentPlaygroundTab agentId={aiAgent?._id} aiAgent={aiAgent} />
                        </TabsContent>
                        <TabsContent value="feedback">
                            <AiAgentFeedbackTab aiAgent={aiAgent} cliente={cliente} />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </div>
    );
}